import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader, Eye, EyeOff } from 'lucide-react';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { sanitizeInput, sanitizeObject } from '../../utils/sanitize';
import FileUpload from '../../components/Common/FileUpload';

const EditUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        mobile: '',
        role: '',
        status: '',
        college: '',
        qualification: '',
        password: '',
        confirmPassword: '',
        profileImage: ''
    });

    useEffect(() => {
        fetchUserDetails();
    }, [id]);

    const fetchUserDetails = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/users/${id}`);
            if (data.success) {
                setFormData({
                    fullName: sanitizeInput(data.data.fullName || ''),
                    email: sanitizeInput(data.data.email || ''),
                    mobile: sanitizeInput(data.data.mobile || ''),
                    role: sanitizeInput(data.data.role || ''),
                    status: sanitizeInput(data.data.status || 'active'),
                    college: sanitizeInput(data.data.college?._id || data.data.college || ''),
                    qualification: sanitizeInput(data.data.qualification || ''),
                    password: '',
                    confirmPassword: '',
                    profileImage: data.data.profileImage || ''
                });
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            if (error.response?.status === 404) {
                toast.error('User not found');
            } else if (error.response?.status === 403) {
                toast.error('You do not have permission to view this user');
            } else if (error.response?.status === 500) {
                toast.error('Server error while loading user details. Please try again later');
            } else if (!navigator.onLine) {
                toast.error('No internet connection. Please check your network');
            } else {
                toast.error('Failed to load user details');
            }
            navigate('/users');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let sanitizedValue = typeof value === 'string' ? sanitizeInput(value) : value;
        
        if (name === 'mobile' && typeof value === 'string') {
             sanitizedValue = value.replace(/\D/g, '').slice(0, 10);
        }

        setFormData(prev => ({
            ...prev,
            [name]: sanitizedValue
        }));
    };

    const validateForm = () => {
        const errors = {};

        // Required field validation
        if (!formData.fullName?.trim()) {
            errors.fullName = 'Full name is required';
        }
        if (!formData.email?.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (!formData.mobile?.trim()) {
            errors.mobile = 'Mobile number is required';
        } else if (!/^[0-9]{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
            errors.mobile = 'Please enter a valid 10-digit mobile number';
        }
        if (!formData.role) {
            errors.role = 'Role is required';
        }
        if (!formData.status) {
            errors.status = 'Status is required';
        }

        if (formData.password) {
            if (formData.password.length < 6) {
                errors.password = 'Password must be at least 6 characters';
            }
            if (formData.password !== formData.confirmPassword) {
                errors.confirmPassword = 'Passwords do not match';
            }
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            Object.values(errors).forEach(error => toast.error(error));
            return;
        }

        try {
            setSaving(true);
            const sanitizedData = sanitizeObject(formData);
            sanitizedData.profileImage = formData.profileImage;
            if (!sanitizedData.password) {
                delete sanitizedData.password;
            }
            delete sanitizedData.confirmPassword;

            const { data } = await api.put(`/users/${id}`, sanitizedData);
            if (data.success) {
                toast.success('User updated successfully');
                if (formData.role === 'support') {
                    navigate('/it-support');
                } else if (formData.role === 'recruiter') {
                    navigate('/admin/recruiters');
                } else {
                    navigate('/users');
                }
            }
        } catch (error) {
            console.error('Error updating user:', error);
            if (error.response?.status === 404) {
                toast.error('User not found');
            } else if (error.response?.status === 403) {
                toast.error('You do not have permission to update this user');
            } else if (error.response?.status === 500) {
                toast.error('Server error while updating user. Please try again later');
            } else if (!navigator.onLine) {
                toast.error('No internet connection. Please check your network');
            } else {
                toast.error(error.response?.data?.message || 'Failed to update user');
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader className="w-8 h-8 animate-spin text-[#1a3884]" />
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto p-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={() => {
                        if (formData.role === 'support') {
                            navigate('/it-support');
                        } else if (formData.role === 'recruiter') {
                            navigate('/admin/recruiters');
                        } else {
                            navigate('/users');
                        }
                    }}
                    className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-[#00152E] dark:text-white">Edit User</h1>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Full Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                value={sanitizeInput(formData.fullName)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
                                required
                                maxLength={100}
                                autoComplete="off"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email Address
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={sanitizeInput(formData.email)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 cursor-not-allowed"
                                readOnly
                            />
                        </div>

                        {/* Mobile */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                name="mobile"
                                value={sanitizeInput(formData.mobile)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
                                maxLength={10}
                                autoComplete="off"
                            />
                        </div>

                        {/* Role - Read Only for integrity */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Role
                            </label>
                            <input
                                type="text"
                                value={formData.role ? (formData.role === 'support' ? 'IT SUPPORT' : sanitizeInput(formData.role).replace('_', ' ').toUpperCase()) : 'N/A'}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                readOnly
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Status
                            </label>
                            <select
                                name="status"
                                value={sanitizeInput(formData.status)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        {/* Qualification */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Qualification
                            </label>
                            <input
                                type="text"
                                name="qualification"
                                value={sanitizeInput(formData.qualification)}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
                                maxLength={100}
                                autoComplete="off"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Password (Leave blank to keep current)
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password || ''}
                                    onChange={handleChange}
                                    placeholder="New password"
                                    className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
                                    maxLength={255}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(prev => !prev)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    value={formData.confirmPassword || ''}
                                    onChange={handleChange}
                                    placeholder="Confirm new password"
                                    className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3884] focus:border-transparent"
                                    maxLength={255}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(prev => !prev)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {/* Profile Image Upload */}
                        {formData.role !== 'college_admin' && (
                            <div className="md:col-span-2">
                                <FileUpload
                                    type="image"
                                    label="Profile Image"
                                    onUploadComplete={(fileData) => setFormData(prev => ({ ...prev, profileImage: fileData.url }))}
                                />
                                {formData.profileImage && (
                                    <div className="mt-2 flex items-center gap-3">
                                        <span className="text-sm text-gray-500">Current Image:</span>
                                        <img
                                            src={getFileUrl(formData.profileImage)}
                                            alt="Current Profile"
                                            className="w-12 h-12 object-cover rounded-full border border-gray-200"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => navigate('/users')}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#1a3884] hover:bg-[#00152E] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUser;
